import { createSlice, createEntityAdapter } from '@reduxjs/toolkit';
import { deleteCustomerById } from '../thunks/customers';
import {
  getDeviceById,
  updateDeviceById,
  deleteDeviceById,
  getDevicesByCustomerId,
  getDevicesByCustomerIds,
  getDevicesByWorkspace,
  getDevicesByIds,
} from '../thunks/devices';
import { getCurrentUser } from '../thunks/user';
import { isAfter, parseISO } from 'date-fns';

const devicesAdapter = createEntityAdapter({});

export const devicesSlice = createSlice({
  name: 'devices',
  initialState: devicesAdapter.getInitialState({ status: 'idle', error: null }),
  reducers: {
    setDevices: (state, action) => {
      devicesAdapter.upsertMany(state, action.payload);
    },
    setDevicePosition: (state, action) => {
      const { deviceId } = action.payload;

      devicesAdapter.updateOne(state, {
        id: deviceId,
        changes: {
          lastDevicePosition: {
            ...action.payload,
          },
        },
      });
    },
    setDevicePlace: (state, action) => {
      const {
        id,
        deviceId,
        workspaceId,
        street,
        name,
        city,
        state: stateCountry,
        zipCode,
        countryId,
        country,
        coordinate,
      } = action.payload;

      devicesAdapter.updateOne(state, {
        id: deviceId,
        changes: {
          devicePlaces: [
            {
              placeId: id,
              place: {
                id,
                street,
                zipCode,
                city,
                state: stateCountry,
                countryId,
                country,
                name,
                workspaceId,
                coordinate,
              },
            },
          ],
        },
      });
    },
    setUpdateDevicePlaceOpen: (state, action) => {
      devicesAdapter.updateOne(state, {
        id: action.payload,
        changes: {
          placeOpened: true,
        },
      });
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getCurrentUser.fulfilled, (state) => {
        state.status = 'idle';
      })
      .addCase(getDevicesByWorkspace.fulfilled, (state) => {
        state.status = 'fulfilled';
      })
      .addCase(getDeviceById.fulfilled, (state, action) => {
        devicesAdapter.upsertOne(state, action.payload);
      })
      .addCase(getDevicesByCustomerId.fulfilled, (state, action) => {
        const { devices } = action.payload;
        devicesAdapter.upsertMany(state, devices);
      })
      .addCase(getDevicesByCustomerIds.fulfilled, (state, action) => {
        const { devices } = action.payload;
        devicesAdapter.upsertMany(state, devices);
      })
      .addCase(getDevicesByIds.fulfilled, (state, action) => {
        devicesAdapter.upsertMany(state, action.payload);
      })
      .addCase(deleteDeviceById.fulfilled, (state, action) => {
        const { id } = action.payload;
        devicesAdapter.removeOne(state, id);
      })
      .addCase(updateDeviceById.fulfilled, (state, action) => {
        const { id } = action.payload;
        devicesAdapter.updateOne(state, {
          id,
          changes: action.payload,
        });
      })
      .addCase(deleteCustomerById.fulfilled, (state, action) => {
        const { id: deletedCustomerId } = action.payload;

        const deletedCustomerDevices = state.ids.filter(
          (deviceId) =>
            state.entities[deviceId].customerId === deletedCustomerId
        );

        devicesAdapter.removeMany(state, deletedCustomerDevices);
      });
  },
});

export const {
  setDevices,
  setDevice,
  setDevicePosition,
  setDevicePlace,
  setUpdateDevicePlaceOpen,
} = devicesSlice.actions;

export const devicesAdapterSelectors = devicesAdapter.getSelectors(
  (state) => state.devices
);

export default devicesSlice.reducer;
